import { DashboardTileType, IDashboardTileComponentProps, IInfoTileInfo } from "@components/dashboard";
import InfoTile, { infoTileCurrencyFormatter } from "@components/tiles/infoTile/InfoTile";
import {
    GroupedInfoTileBackground,
    GroupedInfoTileContentWrapper,
    GroupInfoTileHeader,
} from "@components/tiles/infoTile/InfoTile.styles";
import { VatStatementFrequencyCode } from "@odata/GeneratedEnums";
import { getVatFrequencyByDate } from "@pages/admin/vatRules/VatRules.utils";
import customFetch from "@utils/customFetch";
import i18next from "i18next";
import React, { memo, useContext, useEffect } from "react";

import { VAT_OVERVIEW_URL } from "../../../constants";
import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { Status } from "../../../enums";
import { getUtcDayjs } from "../../../types/Date";

interface IProps extends IDashboardTileComponentProps {
}

const VatOverview: React.FC<IProps> = ({ inEditMode }) => {
    const context = useContext(AppContext);
    const [data, setData] = React.useState(null);

    useEffect(() => {
        customFetch(VAT_OVERVIEW_URL).then((res) => {
            if (res.ok) {
                res.json().then((data) => {
                    setData(data);
                }).catch((err) => {
                    console.error("Error parsing JSON:", err);
                });
            }
        });
    }, []);

    const t = (key: string) => i18next.t(`Home:VatOverview.${key}`);
    const _getTextFrequency = (start: string) => {
        const frequency = getVatFrequencyByDate(context, start);
        return frequency === VatStatementFrequencyCode.Quarterly ? "Quarter": "Month";
    };

    const preparedData = [{
        value: data?.CurrentPeriodAmount,
        unit: "Kč",
        title: t(`Actual${_getTextFrequency(data?.CurrentPeriodDateRange?.Start)}`),
        label: data?.CurrentPeriodName
    }, {
        value: data?.LastClosedPeriodAmount,
        title: t(`LastClosed${_getTextFrequency(data?.LastClosedPeriodDateRange?.Start)}`),
        unit: "Kč",
        label: `${data?.LastClosedPeriodName ?? ""} (${data?.IsLastClosedPeriodCleared ? t("Paid") : t("NotPaid")})`
    }, {
        value: data?.PreviousPeriodAmount,
        title: t(`Previous${_getTextFrequency(data?.PreviousPeriodDateRange?.Start)}`),
        unit: "Kč",
        label: data?.PreviousPeriodName
    }, {
        value: Math.abs(data?.VatBalance),
        title: t("VatPaid"),
        unit: "Kč",
        label: data?.VatBalance === 0 ? t("AlreadyPaid") : data?.VatBalance > 0 ? t("ToPay") : t("Own"),
        severity: data?.VatBalance === 0 ? null : data?.VatBalance > 0 ? Status.Success : Status.Error
    }];

    const getVatInfo = (index: number): IInfoTileInfo => {
        return {
            type: DashboardTileType.Info,
            title: preparedData[index].title,
            formatter: infoTileCurrencyFormatter,
            size: { w: 1, h: 1 },
            infoData: [preparedData[index]]
        };
    };

    return (
        <GroupedInfoTileBackground>
            <GroupInfoTileHeader>
                {t("Title")}
            </GroupInfoTileHeader>

            <GroupedInfoTileContentWrapper cols={2} rows={2}>
                {preparedData.map((item, index) => (
                    <InfoTile inEditMode={inEditMode} info={getVatInfo(index)} isGrouped/>
                ))}
            </GroupedInfoTileContentWrapper>
        </GroupedInfoTileBackground>
    );
};

export default memo(VatOverview);